import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArtistDTO, Song } from '../../view/spotipitan/types';
import { handleCopy, shuffleList } from '../../../utils/utils';
import toast from 'react-hot-toast';
import useSongStore from '../../view/spotipitan/store/SongState';
import { DiscordBotApi } from '../../../utils/discordBotApi';
import './styles.scss';
import { OptionButton } from '../option-button';
import { Spotip17anFunctions } from '../../../utils/api';
import ImageMosaic from '../image-mosaic/image-mosaic';
import { getPlaylistBdStatus } from "../../../utils/dbUtils";
import ScrollModalView, { ScrollModalApi } from "../scroll-modal/view";
import SquareImage from '../square-image/square-image';
import { auth } from '../../../firebase';

type SortOption = 'username-asc' | 'username-desc' | 'songs_count-asc' | 'songs_count-desc';

export type PlaylistListPropsExtraOption = {
    text: string;
    icon: React.ReactNode;
    onClick: (playlist: ArtistDTO) => void;
}

interface PlaylistListProps {
    inputPlaylists: ArtistDTO[];
    onPlaylistClick?: (playlist: ArtistDTO) => void;
    offlineMode?: boolean;
    extraOptions?: PlaylistListPropsExtraOption[];
    onChangeSort?: (sort: SortOptionValue[]) => boolean;
    loading?: boolean;
    style?: React.CSSProperties;
    customSearchQuery?: string;
}

export type SortOptionValue = {
    name: string;
    value: string;
}

export type PlaylistListApi = {
    getSortBy: () => SortOptionValue[];
}

const PlaylistList = forwardRef<PlaylistListApi, PlaylistListProps>(({
    inputPlaylists,
    onPlaylistClick,
    extraOptions = [],
    onChangeSort,
    offlineMode = false,
    loading = false,
    style = {},
    customSearchQuery = ""
}, ref) => {
    const {
        setOriginalQueueOrder,
        setLoopMode,
        setRandomQueue,
        setQueue,
        playNextSong,
        emptyQueue,
        setCurrentSong,
        emptyHistory,
        state: songState
    } = useSongStore(state => state);
    const { isBotMode, botGuildId, botVoiceChannelId } = songState;
    const [selectedPlaylist, setSelectedPlaylist] = React.useState<ArtistDTO | null>(null);
    const [loadingPlaylist, setLoadingPlaylist] = React.useState(false);
    const [sortBy, setSortBy] = useState<SortOption>('username-asc');
    const [searchQuery, setSearchQuery] = useState('');
    const scrollRef = useRef<ScrollModalApi>(null);
    const [playlists, setPlaylists] = useState<ArtistDTO[]>([]);
    const currentSort = useRef<SortOptionValue[]>([{ name: "sortBy", value: '' }, { name: "search", value: '' }]);
    const [filteredAndSortedPlaylists, setFilteredAndSortedPlaylists] = useState<ArtistDTO[]>(playlists);

    useEffect(() => {
        setSearchQuery(customSearchQuery || "")
    }, [customSearchQuery])

    useEffect(() => {
        const filterLoadedPlaylist = () => {
            setFilteredAndSortedPlaylists(() => {
                return [...playlists]
                    .filter(playlist =>
                        playlist.username.toLowerCase().includes(searchQuery.toLowerCase())
                    );
            });
        }

        const changedSort = currentSort.current[0].value !== sortBy || currentSort.current[1].value !== searchQuery;

        if (!onChangeSort || !changedSort) {
            filterLoadedPlaylist();
            return;
        }

        currentSort.current = [{ name: "sortBy", value: sortBy }, { name: "search", value: searchQuery }];
        if (onChangeSort(currentSort.current)) return;

        filterLoadedPlaylist();

    }, [sortBy, searchQuery, playlists]);

    useEffect(() => {

        const loadPlaylists = async () => {
            const auxPlaylists: ArtistDTO[] = [];

            for (const playlist of inputPlaylists) {
                playlist.downloaded_status = offlineMode ? undefined : await getPlaylistBdStatus(playlist.id);
                auxPlaylists.push(playlist);
            }

            setPlaylists(auxPlaylists);
        }

        loadPlaylists();

    }, [inputPlaylists, offlineMode]);

    const handlePlay = async (playlist: ArtistDTO) => {
        try {
            setLoadingPlaylist(true);
            const playlistDetails = offlineMode ? playlist : await Spotip17anFunctions.use('fetchPlaylist').call({ playlistId: playlist.id + "" });

            if (!playlistDetails.songs?.length) {
                toast.error('La playlist está vacía');
                return;
            }

            if (isBotMode && botGuildId) {
                (async () => {
                    try {
                        await DiscordBotApi.stop(botGuildId);
                    } catch (e) {
                        console.warn("Could not stop bot before playing playlist:", e);
                    }
                    DiscordBotApi.play(
                        botGuildId,
                        `https://spotip17an.es/playlist/${playlist.id}`,
                        botVoiceChannelId || undefined
                    ).then(() => {
                        toast.success("Playlist enviada al bot");
                    }).catch(e => console.error("Error playing playlist on bot:", e));
                })();
                scrollRef.current?.close();
                return;
            }

            emptyQueue();
            setCurrentSong(undefined);
            setLoopMode('none');
            setRandomQueue(false);
            setOriginalQueueOrder(playlistDetails.songs.map((x: Song) => x.id));
            setQueue(playlistDetails.songs);
            playNextSong();
            emptyHistory();
            scrollRef.current?.close();
        } catch (error) {
            toast.error('Error al cargar la playlist');
            console.error(error);
        } finally {
            setLoadingPlaylist(false);
        }
    };

    const handleShuffle = async (playlist: ArtistDTO) => {
        try {
            setLoadingPlaylist(true);
            const playlistDetails = offlineMode ? playlist : await Spotip17anFunctions.use('fetchPlaylist').call({ playlistId: playlist.id + "" });

            if (!playlistDetails.songs?.length) {
                toast.error('La playlist está vacía');
                return;
            }

            if (isBotMode && botGuildId) {
                const playAndShuffle = async () => {
                    try {
                        await DiscordBotApi.stop(botGuildId);
                        await DiscordBotApi.play(
                            botGuildId,
                            `https://spotip17an.es/playlist/${playlist.id}`,
                            botVoiceChannelId || undefined
                        );
                        await new Promise(resolve => setTimeout(resolve, 800));
                        await DiscordBotApi.shuffle(botGuildId);
                        toast.success("Playlist enviada y mezclada en el bot");
                    } catch (e) {
                        console.error("Error shuffling playlist on bot:", e);
                    }
                };
                playAndShuffle();
                scrollRef.current?.close();
                return;
            }

            emptyQueue();
            setCurrentSong(undefined);
            setLoopMode('none');
            setRandomQueue(true);
            setOriginalQueueOrder(playlistDetails.songs.map((x: Song) => x.id));
            setQueue(shuffleList(playlistDetails.songs));
            playNextSong();
            emptyHistory();
            scrollRef.current?.close();
        } catch (error) {
            toast.error('Error al cargar la playlist');
            console.error(error);
        } finally {
            setLoadingPlaylist(false);
        }
    };

    const handleShare = (playlist: ArtistDTO) => {
        let shareUrl = window.location.href;
        const playlistIdStr = String(playlist.id || "");
        const user = auth.currentUser;
        if (playlistIdStr === "favs" && (user?.uid || playlist.creator_uid)) {
            const uid = user?.uid || playlist.creator_uid;
            shareUrl = `${window.location.origin}/playlist/favs-${uid}`;
        }
        handleCopy(shareUrl);
        scrollRef.current?.close();
    };

    const handleContextMenu = (e: React.MouseEvent, playlist: ArtistDTO) => {
        e.preventDefault();
        setSelectedPlaylist(playlist);
        scrollRef.current?.open({ x: e.clientX, y: e.clientY });
    };

    useImperativeHandle(ref, () => ({
        getSortBy: () => [
            { name: "sortBy", value: sortBy },
            { name: "search", value: searchQuery },
        ],
    }));

    return (
        <>
            <div className="playlist-list" style={style}>
                <div className="playlist-list__filters">
                    <div className="playlist-list__search">
                        <i className="fa-solid fa-search"></i>
                        <input
                            type="text"
                            placeholder="Buscar playlist..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <div className="playlist-list__sort">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            className="form-select form-select-sm"
                        >
                            <option value="username-asc">Nombre (A-Z)</option>
                            <option value="username-desc">Nombre (Z-A)</option>
                            <option value="songs_count-asc">Menos canciones</option>
                            <option value="songs_count-desc">Más canciones</option>
                        </select>
                    </div>
                </div>
                {filteredAndSortedPlaylists.map((playlist) => (
                    <div
                        key={playlist.id}
                        className="playlist-list__item"
                        onContextMenu={(e) => handleContextMenu(e, playlist)}
                    >
                        <div className="playlist-list__item-content" onClick={() => onPlaylistClick?.(playlist)}>
                            <div className="playlist-list__item-image">
                                {playlist.poster_url || playlist.songs?.length === 1 ? (
                                    <SquareImage hideBackground={true} playlist={playlist} song={playlist.poster_url ? undefined : playlist.songs?.[0]} alt={playlist.username} />
                                ) : (
                                    <ImageMosaic
                                        songs={playlist.songs?.slice(0, 4) || []} />
                                )}
                            </div>
                            <div className="playlist-list__item-info">
                                <h3>{playlist.username}</h3>
                                <span>{playlist.songs_count || 0} canciones

                                    {playlist.downloaded_status === 'downloading' &&
                                        <i className="fa-solid fa-spinner fa-spin ms-2"></i>}
                                    {playlist.downloaded_status === 'updated' &&
                                        <i className="fa-solid fa-circle-down ms-2"></i>}
                                    {playlist.downloaded_status === 'downloaded' &&
                                        <i className="fa-solid fa-circle-check ms-2"></i>}
                                    {playlist.downloaded_status === 'error' &&
                                        <i className="fa-solid fa-circle-exclamation ms-2"></i>}

                                </span>
                            </div>
                            <button
                                className="playlist-list__item-options"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleContextMenu(e, playlist);
                                }}
                            >
                                <i className="fa-solid fa-ellipsis"></i>
                            </button>
                        </div>
                    </div>
                ))}
                {!filteredAndSortedPlaylists.length && !loading && (
                    <div style={{
                        fontSize: "13px",
                        opacity: 0.7
                    }}>
                        No se han encontrado playlists
                    </div>
                )}
                {loading && (
                    <div className="d-flex justify-content-center py-3">
                        <div className="spinner-border text-light" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
            </div>

            <ScrollModalView
                ref={scrollRef}
                header={
                    <div className="playlist-modal-header">
                        {selectedPlaylist && <div className="playlist-modal-header__image">
                            {selectedPlaylist.poster_url || selectedPlaylist.songs?.length === 1 ? (
                                <SquareImage hideBackground={true} playlist={selectedPlaylist} song={selectedPlaylist.poster_url ? undefined : selectedPlaylist.songs?.[0]} alt={selectedPlaylist.username} />
                            ) : (
                                <ImageMosaic
                                    songs={selectedPlaylist.songs?.slice(0, 4) || []} />
                            )}
                        </div>}
                        <h3 className="mb-0">{selectedPlaylist?.username}</h3>
                    </div>
                }
            >
                {selectedPlaylist && (
                    <div className="playlist-options">
                        <OptionButton
                            text="Reproducir"
                            icon={<i className="fa-solid fa-play"></i>}
                            onClick={() => handlePlay(selectedPlaylist)}
                            hidden={loadingPlaylist}
                        />
                        <OptionButton
                            text="Reproducir aleatoriamente"
                            icon={<i className="fa-solid fa-shuffle"></i>}
                            onClick={() => handleShuffle(selectedPlaylist)}
                            hidden={loadingPlaylist}
                        />
                        {!offlineMode && <OptionButton
                            text="Compartir"
                            icon={<i className="fa-solid fa-share-nodes"></i>}
                            onClick={() => handleShare(selectedPlaylist)}
                            hidden={loadingPlaylist}
                        />}
                        {extraOptions.map((option, index) => (
                            <OptionButton
                                key={index}
                                text={option.text}
                                icon={option.icon}
                                onClick={() => {
                                    option.onClick(selectedPlaylist);
                                    scrollRef.current?.close();
                                }}
                                hidden={loadingPlaylist}
                            />
                        ))}
                        {loadingPlaylist && (
                            <div className="d-flex justify-content-center py-3">
                                <div className="spinner-border text-success" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ScrollModalView>
        </>
    );
});

export default PlaylistList; 
